import React from "react";

import { responseCancelJob } from "@api/job/cancelJob";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
  Chip,
  Grid,
  Divider,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";

import { useAppSelector } from "@app/hooks";

import { handleApiError } from "@features/config";

import CommonDialog from "@components/Dialog/CommonDialog";
import CustomConfirmationDialog from "@components/Dialog/CustomConfirmationDialog";

import { useSnackbar } from "@contexts/SnackbarContext";

import queryClient from "@config/queryClient";

import { CancelQuestColor, CancelRequestStatus } from "./type";

// Define types for props
type RequestDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  jobId: string;
  requestDetails: {
    requestor: {
      name: string;
      id: string;
      avatar: string;
    };
    status: CancelRequestStatus;
    timeRequested: string;
    reason: string;
  };
};
const RequestDetailsDialog = ({
  open,
  onClose,
  jobId,
  requestDetails,
}: RequestDetailsDialogProps) => {
  const user = useAppSelector((state) => state.auth.userInfo.user);
  const [openConfirm, setOpenConfirm] = React.useState("");
  const { mutate: acceptCancelRequest, isLoading } = useMutation({
    mutationFn: responseCancelJob,
  });
  const { setSnackbar } = useSnackbar();

  const onSendAnswer = async (answer: "YES" | "NO") => {
    await acceptCancelRequest(
      {
        jobId,
        answer,
      },
      {
        onSuccess: (res) => {
          if (answer === "YES") {
            const isPaidUser = res?.data?.paidByUserId === user?.id;
            setSnackbar({
              message: isPaidUser
                ? res?.data.type == "card"
                  ? `The quest has been successfully canceled. The refund will be processed to your Card - ${res?.data.card} within a few days.`
                  : "The quest has been successfully canceled. The refund will be processed to your Deposit account"
                : "The quest has been successfully canceled.",
              open: true,
              severity: "success",
            });
          } else {
            setSnackbar({
              message:
                "Your request has been declined. Your request will stay in In progress. No refund will be issued.",
              open: true,
              severity: "success",
            });
          }
          queryClient.invalidateQueries(["getCancelJobRequestList", jobId]);
          queryClient.invalidateQueries(["getJobsByProject"]);
          onClose();
        },
        onError: (error: any) => {
          const errorMessage = handleApiError(error).message;
          setSnackbar({
            message: `An error occurred while canceling the quest. Please try again later. \n ${errorMessage}`,
            open: true,
            severity: "error",
          });
        },
      }
    );
  };

  return (
    <CommonDialog
      isOpen={open}
      title="Request details"
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: 600,
        },
      }}
    >
      <Grid container spacing={2} alignItems="center" mb={2} px={2}>
        {/* Requestor Info */}
        <Grid item xs={4}>
          <Avatar src={requestDetails.requestor.avatar} />
        </Grid>
        <Grid item xs={8}>
          <Typography fontWeight="bold">{requestDetails.requestor.name}</Typography>
        </Grid>

        {/* Status */}
        <Grid item xs={4}>
          <Typography fontWeight="bold">Status</Typography>
        </Grid>
        <Grid item xs={8}>
          <Chip
            label={requestDetails.status}
            sx={{
              textTransform: "capitalize",
              color: CancelQuestColor.color[requestDetails.status],
              backgroundColor: CancelQuestColor.bgColor[requestDetails.status],
            }}
          />
        </Grid>

        {/* Time Requested */}
        <Grid item xs={4}>
          <Typography fontWeight="bold">Time requested</Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography>
            {dayjs(requestDetails.timeRequested).format("MMM D, YYYY HH:mm [GMT] Z")}
          </Typography>
        </Grid>

        {/* Reason */}
        <Grid item xs={4}>
          <Typography fontWeight="bold" gutterBottom>
            Reason
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography>{requestDetails.reason}</Typography>
        </Grid>
      </Grid>
      {requestDetails.requestor.id !== user.id &&
        requestDetails.status === CancelRequestStatus.Pending && (
          <DialogActions sx={{ p: 0 }}>
            <Divider sx={{ my: 2 }} />
            <Button
              disabled={isLoading}
              onClick={() => setOpenConfirm("disagree")}
              variant="outlined"
            >
              Disagree
            </Button>
            <Button
              disabled={isLoading}
              onClick={() => setOpenConfirm("agree")}
              variant="contained"
            >
              Agree
            </Button>
          </DialogActions>
        )}

      <CustomConfirmationDialog
        title={openConfirm === "agree" ? "Accept request" : "Cancel request"}
        content={
          openConfirm === "agree"
            ? "Accept to this request means the quest's budget will be refunded. Are you sure you want to proceed?"
            : "Declining this request means the quest will proceed as planned. Are you sure you want to continue?"
        }
        buttons={[
          {
            variant: "outlined",
            label: "Not yet",
          },
          {
            label: "Submit now",
            handleOnClickButton: () =>
              onSendAnswer(openConfirm === "agree" ? "YES" : "NO"),
            //prevent unmount before the mutation finishes
          },
        ]}
        openDialog={!!openConfirm}
        setOpenDialog={() => setOpenConfirm("")}
      />
    </CommonDialog>
  );
};

export default RequestDetailsDialog;
